import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api.js'
import { money } from '../../lib/format.js'
import { Badge, Drawer, DrawerSection, Field, Notice, RowsSkeleton, Segmented, Switch } from '../../components/ui.js'
import { ConfirmDialog, toast } from '../../components/feedback.js'
import { MailIcon, SparkIcon, WhatsAppIcon } from '../../components/icons.js'
import {
  newIdempotencyKey,
  type ApprovedTemplate,
  type CampaignView,
  type Channel,
  type EmailContent,
  type PreviewPayload,
  type Selection,
  type SelectionPreview,
  type Slot,
  type TemplatesPayload,
  type WhatsappContent,
} from './types.js'

/**
 * The campaign composer: who, what, when, and a final yes.
 *
 * Every number on it — who is eligible, why the rest are not, what it costs,
 * what the message will say — comes from the server, which resolves the
 * selection and renders the message the same way the send will. The submit
 * carries the eligible count the merchant confirmed; if more shoppers would
 * now be messaged, the server refuses rather than surprising them.
 */

const DEFAULT_SLOTS = ['customer_name', 'cart_value', 'checkout_url', 'product_name', 'store_name']

export function defaultSlots(count: number | null): Slot[] {
  const wanted = Math.max(0, count ?? 3)
  const mapped: Slot[] = DEFAULT_SLOTS.slice(0, wanted).map((key) => ({ source: 'variable', key }))
  const extra: Slot[] = Array.from({ length: Math.max(0, wanted - DEFAULT_SLOTS.length) }, () => ({ source: 'text', value: '' }))
  return [...mapped, ...extra]
}

export function useDebounced<T>(value: T, ms = 400): T {
  const [current, setCurrent] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setCurrent(value), ms)
    return () => window.clearTimeout(timer)
  }, [value, ms])
  return current
}

/** Maps an approved template's numbered slots to variables or short fixed text. Shared with Automation. */
export function SlotEditor({
  template,
  slots,
  variables,
  onChange,
}: {
  template: ApprovedTemplate | undefined
  slots: Slot[]
  variables: TemplatesPayload['variables']
  onChange: (next: Slot[]) => void
}): ReactNode {
  if (!template) return null

  return (
    <div className="slot-list">
      {template.variables === 0 && <p className="muted">This template has no variables.</p>}
      {slots.map((slot, index) => (
        <div key={index} className="slot-row">
          <span className="slot-row__num">{`{{${index + 1}}}`}</span>
          <select
            className="select"
            aria-label={`Value for {{${index + 1}}}`}
            value={slot.source === 'variable' ? slot.key : '__text'}
            onChange={(event) => {
              const next = [...slots]
              next[index] = event.target.value === '__text' ? { source: 'text', value: '' } : { source: 'variable', key: event.target.value }
              onChange(next)
            }}
          >
            {variables.map((variable) => (
              <option key={variable.key} value={variable.key}>
                {variable.label}
              </option>
            ))}
            <option value="__text">Fixed text…</option>
          </select>
          {slot.source === 'text' && (
            <input
              className="input"
              aria-label={`Fixed text for {{${index + 1}}}`}
              maxLength={200}
              value={slot.value}
              onChange={(event) => {
                const next = [...slots]
                next[index] = { source: 'text', value: event.target.value }
                onChange(next)
              }}
            />
          )}
        </div>
      ))}
    </div>
  )
}

export function EmailEditor({ value, variables, onChange }: { value: EmailContent; variables: TemplatesPayload['variables']; onChange: (next: EmailContent) => void }): ReactNode {
  return (
    <div className="stack--sm">
      <Field label="Subject">
        <input className="input" maxLength={150} value={value.subject} onChange={(event) => onChange({ ...value, subject: event.target.value })} />
      </Field>
      <Field label="Message" hint="Plain text. Blank lines start a new paragraph; {{checkout_url}} becomes a button.">
        <textarea className="textarea" rows={8} maxLength={5000} value={value.body} onChange={(event) => onChange({ ...value, body: event.target.value })} />
      </Field>
      <div className="var-chips" aria-label="Insert a variable">
        {variables.map((variable) => (
          <button key={variable.key} type="button" className="chip" onClick={() => onChange({ ...value, body: `${value.body}{{${variable.key}}}` })} title={variable.label}>
            {`{{${variable.key}}}`}
          </button>
        ))}
      </div>
    </div>
  )
}

type AiResult = { email?: Array<{ subject: string; body: string; note: string }>; whatsapp?: Array<{ template: string; variables: string[]; note: string }>; dropped: string[] }

export function Composer({
  open,
  onClose,
  selection,
  selectedCount,
  initialChannel,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  selection: Selection | null
  selectedCount: number
  initialChannel: Channel
  onCreated: (campaign: CampaignView) => void
}): ReactNode {
  const [channel, setChannel] = useState<Channel>(initialChannel)
  const [name, setName] = useState('')
  const [cooldownHours, setCooldownHours] = useState(24)
  const [excludeContacted, setExcludeContacted] = useState(false)
  const [whatsapp, setWhatsapp] = useState<WhatsappContent | null>(null)
  const [email, setEmail] = useState<EmailContent | null>(null)
  const [couponCode, setCouponCode] = useState('')
  const [when, setWhen] = useState<'now' | 'later'>('now')
  const [scheduledAt, setScheduledAt] = useState('')
  const [batchSize, setBatchSize] = useState(50)
  const [ratePerMinute, setRatePerMinute] = useState(60)
  const [maxRetries, setMaxRetries] = useState(2)
  const [confirming, setConfirming] = useState(false)
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey('camp'))
  const [ai, setAi] = useState<AiResult | null>(null)

  // A fresh composer is a fresh campaign: new key, channel from the button pressed.
  useEffect(() => {
    if (!open) return
    setChannel(initialChannel)
    setName(`Recovery ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`)
    setIdempotencyKey(newIdempotencyKey('camp'))
    setAi(null)
    setConfirming(false)
  }, [open, initialChannel])

  const templates = useQuery({ queryKey: ['abandoned-templates'], queryFn: () => api<TemplatesPayload>('/v1/abandoned/templates'), enabled: open, staleTime: 120_000 })
  const approved = useMemo(() => (templates.data?.whatsapp.templates ?? []).filter((template) => template.status === 'approved'), [templates.data])

  // Sensible starting content once the catalogue is known.
  useEffect(() => {
    if (!templates.data) return
    if (!whatsapp && approved.length > 0) {
      const preferred = approved.find((template) => template.name === templates.data.whatsapp.defaultTemplate) ?? approved[0]!
      setWhatsapp({ template: preferred.name, language: preferred.language ?? templates.data.whatsapp.language, variables: defaultSlots(preferred.variables) })
    }
    if (!email && templates.data.email.templates[0]) {
      const starter = templates.data.email.templates[0]
      setEmail({ subject: starter.subject, body: starter.body })
    }
  }, [templates.data, approved, whatsapp, email])

  const selectedTemplate = approved.find((template) => template.name === whatsapp?.template)

  const audience = useQuery({
    queryKey: ['abandoned-selection-preview', selection, channel, cooldownHours, excludeContacted],
    queryFn: () => api<SelectionPreview>('/v1/abandoned/selection/preview', { method: 'POST', body: { selection, channel, cooldownHours, excludeContacted } }),
    enabled: open && selection !== null,
  })

  const content = channel === 'whatsapp' ? { whatsapp: whatsapp ?? undefined } : { email: email ?? undefined }
  const previewInput = useDebounced(JSON.stringify({ channel, content, couponCode: couponCode.trim() || null, sessionId: audience.data?.representativeId ?? null }))

  const preview = useQuery({
    queryKey: ['abandoned-message-preview', previewInput],
    queryFn: () => api<PreviewPayload>('/v1/abandoned/campaigns/preview', { method: 'POST', body: JSON.parse(previewInput) as object }),
    enabled: open && (channel === 'whatsapp' ? Boolean(whatsapp) : Boolean(email)),
  })

  const suggest = useMutation({
    mutationFn: (style: string) =>
      api<AiResult>('/v1/abandoned/ai/suggest', { method: 'POST', body: { channel, style, sessionId: audience.data?.representativeId ?? null, couponCode: couponCode.trim() || null } }),
    onSuccess: (result) => setAi(result),
    onError: (error) => toast('error', 'No suggestions', (error as Error).message),
  })

  const create = useMutation({
    mutationFn: () =>
      api<CampaignView>('/v1/abandoned/campaigns', {
        method: 'POST',
        body: {
          name: name.trim() || 'Recovery campaign',
          channel,
          selection,
          content,
          couponCode: couponCode.trim() || null,
          scheduledAt: when === 'later' && scheduledAt ? new Date(scheduledAt).toISOString() : null,
          batchSize,
          ratePerMinute,
          maxRetries,
          cooldownHours,
          excludeContacted,
          confirmedRecipients: audience.data?.eligible ?? 0,
          idempotencyKey,
        },
      }),
    onSuccess: (campaign) => {
      setConfirming(false)
      toast('ok', campaign.status === 'scheduled' ? 'Campaign scheduled' : 'Campaign started', `${campaign.recipientCount} shoppers will be messaged. Progress is under Campaigns.`)
      onCreated(campaign)
    },
    onError: (error) => {
      setConfirming(false)
      // The audience changed under us: show the new numbers rather than retrying blind.
      if (error instanceof ApiError && error.is('abandoned.recipients_changed')) void audience.refetch()
      toast('error', 'Campaign not created', (error as Error).message)
    },
  })

  const problems = [
    ...(preview.data?.errors ?? []),
    ...(when === 'later' && !scheduledAt ? ['Pick a date and time to schedule.'] : []),
    ...(when === 'later' && scheduledAt && new Date(scheduledAt) <= new Date() ? ['The scheduled time is in the past.'] : []),
    ...(audience.data && !audience.data.walletCovers ? ['Your wallet balance does not cover this campaign.'] : []),
  ]
  const eligible = audience.data?.eligible ?? 0
  const ready = eligible > 0 && problems.length === 0 && !preview.isFetching && !audience.isFetching

  const channelReady = channel === 'whatsapp' ? templates.data?.whatsapp.configured : templates.data?.email.configured

  return (
    <Drawer
      open={open}
      onClose={onClose}
      wide
      title={`New ${channel === 'whatsapp' ? 'WhatsApp' : 'email'} campaign`}
      subtitle={`${selectedCount.toLocaleString('en-IN')} checkout${selectedCount === 1 ? '' : 's'} selected`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" disabled={!ready || create.isPending} onClick={() => setConfirming(true)}>
            {when === 'later' ? 'Review & schedule' : `Review & send to ${eligible.toLocaleString('en-IN')}`}
          </button>
        </>
      }
    >
      <div className="composer">
        <div className="composer__form">
          <DrawerSection title="Channel">
            <Segmented
              label="Channel"
              value={channel}
              onChange={setChannel}
              options={[
                { key: 'whatsapp', label: 'WhatsApp' },
                { key: 'email', label: 'Email' },
              ]}
            />
            {templates.data && !channelReady && (
              <Notice kind="warn" title={`${channel === 'whatsapp' ? 'WhatsApp' : 'Email'} is not set up`}>
                {channel === 'whatsapp' ? templates.data.whatsapp.error ?? 'Connect WhatsApp under Engagement → WhatsApp.' : 'Email sending is not configured on this deployment.'}
              </Notice>
            )}
            <Field label="Campaign name">
              <input className="input" maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
            </Field>
          </DrawerSection>

          <DrawerSection title="Audience">
            {audience.isPending && <RowsSkeleton rows={3} />}
            {audience.error && <Notice kind="error" title="Could not check the audience">{(audience.error as Error).message}</Notice>}
            {audience.data && (
              <>
                <div className="audience">
                  <div>
                    <strong className="audience__num">{audience.data.eligible.toLocaleString('en-IN')}</strong>
                    <small>will receive it</small>
                  </div>
                  <div>
                    <strong className="audience__num audience__num--muted">{audience.data.ineligible.toLocaleString('en-IN')}</strong>
                    <small>will be skipped</small>
                  </div>
                </div>
                {Object.keys(audience.data.reasons).length > 0 && (
                  <ul className="reason-list">
                    {Object.entries(audience.data.reasons).map(([reason, count]) => (
                      <li key={reason}>
                        <span>{audience.data.reasonLabels[reason] ?? reason}</span>
                        <Badge tone="warn">{count}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
                {audience.data.sampleIneligible.length > 0 && (
                  <details className="sample">
                    <summary>Who is skipped</summary>
                    <ul>
                      {audience.data.sampleIneligible.map((entry) => (
                        <li key={entry.id}>
                          {entry.name ?? 'Guest shopper'} — <span className="muted">{audience.data.reasonLabels[entry.reason] ?? entry.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                <p className="muted small">Recovered shoppers, opted-out contacts and checkouts with no link back to the cart are never messaged.</p>
              </>
            )}
            <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
              <label className="check">
                <input type="checkbox" checked={excludeContacted} onChange={(event) => setExcludeContacted(event.target.checked)} />
                Exclude anyone already contacted
              </label>
              <Field label="Skip if contacted on this channel within (hours)">
                <input className="input" style={{ width: 90 }} inputMode="numeric" value={cooldownHours} onChange={(event) => setCooldownHours(Math.min(720, Math.max(0, Number(event.target.value.replace(/\D/g, '')) || 0)))} />
              </Field>
            </div>
          </DrawerSection>

          <DrawerSection
            title="Message"
            action={
              templates.data?.ai ? (
                <span className="row" style={{ gap: 4 }}>
                  <SparkIcon size={14} />
                  {(['friendly', 'professional', 'short', 'personalized'] as const).map((style) => (
                    <button key={style} type="button" className="btn btn--sm" disabled={suggest.isPending || (channel === 'whatsapp' && approved.length === 0)} onClick={() => suggest.mutate(style)}>
                      {style[0]!.toUpperCase() + style.slice(1)}
                    </button>
                  ))}
                </span>
              ) : undefined
            }
          >
            {templates.isPending && <RowsSkeleton rows={4} />}

            {channel === 'whatsapp' && templates.data && (
              approved.length === 0 ? (
                <Notice kind="warn" title="No approved WhatsApp templates">
                  WhatsApp only lets businesses start a conversation with an approved template. Get one approved in Wakit, then come back.
                </Notice>
              ) : (
                <>
                  <Field label="Approved template" hint="Only templates WhatsApp has approved can be sent. Pending or rejected ones are listed but cannot be chosen.">
                    <select
                      className="select"
                      value={whatsapp?.template ?? ''}
                      onChange={(event) => {
                        const next = approved.find((template) => template.name === event.target.value)
                        if (next) setWhatsapp({ template: next.name, language: next.language ?? 'en', variables: defaultSlots(next.variables) })
                      }}
                    >
                      {(templates.data.whatsapp.templates ?? []).map((template) => (
                        <option key={`${template.name}-${template.language ?? ''}`} value={template.name} disabled={template.status !== 'approved'}>
                          {template.name} {template.language ? `(${template.language})` : ''} {template.status !== 'approved' ? `— ${template.status}` : ''}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {selectedTemplate?.category === 'MARKETING' && (
                    <p className="muted small">This is a marketing template: WhatsApp expects the shopper to have opted in to hear from you.</p>
                  )}
                  {whatsapp && <SlotEditor template={selectedTemplate} slots={whatsapp.variables} variables={templates.data.variables} onChange={(variables) => setWhatsapp({ ...whatsapp, variables })} />}
                </>
              )
            )}

            {channel === 'email' && templates.data && email && (
              <>
                <Field label="Start from">
                  <select
                    className="select"
                    defaultValue=""
                    onChange={(event) => {
                      const starter = templates.data.email.templates.find((template) => template.key === event.target.value)
                      if (starter) setEmail({ subject: starter.subject, body: starter.body })
                    }}
                  >
                    <option value="" disabled>
                      Choose a starter…
                    </option>
                    {templates.data.email.templates.map((template) => (
                      <option key={template.key} value={template.key}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <EmailEditor value={email} variables={templates.data.variables} onChange={setEmail} />
              </>
            )}

            {ai && (
              <div className="ai-panel" aria-live="polite">
                <strong>
                  <SparkIcon size={14} /> Suggestions — review before using
                </strong>
                {(ai.email ?? []).map((draft, index) => (
                  <div key={index} className="ai-card">
                    <p className="ai-card__subject">{draft.subject}</p>
                    <p className="ai-card__body">{draft.body}</p>
                    <button type="button" className="btn btn--sm" onClick={() => setEmail({ subject: draft.subject, body: draft.body })}>
                      Use this
                    </button>
                  </div>
                ))}
                {(ai.whatsapp ?? []).map((choice, index) => (
                  <div key={index} className="ai-card">
                    <p className="ai-card__subject">{choice.template}</p>
                    <p className="ai-card__body">{choice.variables.map((key, i) => `{{${i + 1}}} → ${key}`).join(' · ')}</p>
                    {choice.note && <p className="muted small">{choice.note}</p>}
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => setWhatsapp({ template: choice.template, language: approved.find((t) => t.name === choice.template)?.language ?? 'en', variables: choice.variables.map((key) => ({ source: 'variable', key })) })}
                    >
                      Use this template
                    </button>
                  </div>
                ))}
                {(ai.email ?? ai.whatsapp ?? []).length === 0 && <p className="muted small">No suggestion passed the checks. Write the message yourself or try another style.</p>}
                {ai.dropped.length > 0 && <p className="muted small">{ai.dropped.length} suggestion{ai.dropped.length === 1 ? ' was' : 's were'} left out for making claims we could not verify.</p>}
              </div>
            )}

            <Field label="Coupon code (optional)" hint="Must be an active coupon on this store. Fills {{coupon_code}}; on Shopify it is applied to the cart link.">
              <input className="input" maxLength={40} value={couponCode} onChange={(event) => setCouponCode(event.target.value.toUpperCase())} />
            </Field>
          </DrawerSection>

          <DrawerSection title="Delivery">
            <Segmented
              label="When to send"
              value={when}
              onChange={setWhen}
              options={[
                { key: 'now', label: 'Send now' },
                { key: 'later', label: 'Schedule' },
              ]}
            />
            {when === 'later' && (
              <Field label="Send at (your local time)">
                <input className="input" type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} />
              </Field>
            )}
            <div className="grid-3">
              <Field label="Batch size">
                <input className="input" inputMode="numeric" value={batchSize} onChange={(event) => setBatchSize(Math.min(500, Math.max(1, Number(event.target.value.replace(/\D/g, '')) || 1)))} />
              </Field>
              <Field label="Messages per minute">
                <input className="input" inputMode="numeric" value={ratePerMinute} onChange={(event) => setRatePerMinute(Math.min(120, Math.max(1, Number(event.target.value.replace(/\D/g, '')) || 1)))} />
              </Field>
              <Field label="Retries on provider outage">
                <select className="select" value={maxRetries} onChange={(event) => setMaxRetries(Number(event.target.value))}>
                  {[0, 1, 2, 3, 5].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <p className="muted small">Retries only happen when the message provably never left. A shopper is never sent the same campaign twice.</p>
          </DrawerSection>
        </div>

        <aside className="composer__side">
          <div className="composer__sticky">
            <h3 className="composer__label">Preview</h3>
            {preview.isPending && <RowsSkeleton rows={4} />}
            {preview.data?.whatsapp && (
              <div className="wa-bubble">
                <span className="wa-bubble__meta">
                  <WhatsAppIcon size={13} /> {preview.data.whatsapp.template}
                </span>
                {preview.data.whatsapp.text ? (
                  <p>{preview.data.whatsapp.text}</p>
                ) : (
                  <ol className="wa-bubble__params">
                    {preview.data.whatsapp.params.map((param, index) => (
                      <li key={index}>
                        <code>{`{{${index + 1}}}`}</code> {param}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
            {preview.data?.email && (
              <div className="mail-preview">
                <span className="wa-bubble__meta">
                  <MailIcon size={13} /> {preview.data.email.subject}
                </span>
                {/* Rendered by the server with every value escaped; shown in a sandboxed frame. */}
                <iframe title="Email preview" sandbox="" srcDoc={preview.data.email.html} className="mail-preview__frame" />
              </div>
            )}
            {audience.data?.representativeId && <p className="muted small">Shown with a real shopper from your selection.</p>}

            {problems.length > 0 && (
              <Notice kind="warn" title="Fix before sending">
                <ul className="plain-list">
                  {problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </Notice>
            )}

            <h3 className="composer__label">Summary</h3>
            <dl className="kv">
              <div className="kv__row">
                <dt>Recipients</dt>
                <dd>{eligible.toLocaleString('en-IN')}</dd>
              </div>
              <div className="kv__row">
                <dt>Price per message</dt>
                <dd>{audience.data ? (Number(audience.data.pricePerMessage) > 0 ? money(audience.data.pricePerMessage) : 'Free') : '—'}</dd>
              </div>
              <div className="kv__row kv__row--total">
                <dt>Estimated cost</dt>
                <dd>{audience.data ? money(audience.data.estimatedCost) : '—'}</dd>
              </div>
              {audience.data?.walletBalance && (
                <div className="kv__row">
                  <dt>Wallet balance</dt>
                  <dd>{money(audience.data.walletBalance)}</dd>
                </div>
              )}
            </dl>
            <p className="muted small">Charged per message actually accepted by WhatsApp. Skipped and failed messages are not charged.</p>
          </div>
        </aside>
      </div>

      <ConfirmDialog
        open={confirming}
        title={when === 'later' ? 'Schedule this campaign?' : 'Send this campaign now?'}
        confirmLabel={when === 'later' ? 'Schedule' : `Send to ${eligible.toLocaleString('en-IN')}`}
        pending={create.isPending}
        onClose={() => setConfirming(false)}
        onConfirm={() => create.mutate()}
        message={
          <>
            <p>
              {eligible.toLocaleString('en-IN')} shopper{eligible === 1 ? '' : 's'} will get a {channel === 'whatsapp' ? `WhatsApp message (${whatsapp?.template ?? ''})` : 'reminder email'}
              {when === 'later' && scheduledAt ? ` on ${new Date(scheduledAt).toLocaleString('en-IN')}` : ''}.
            </p>
            <p>Estimated cost: {audience.data ? money(audience.data.estimatedCost) : '—'}. This cannot be undone once messages have gone out.</p>
          </>
        }
      />
    </Drawer>
  )
}
